import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { standbyService } from '../../../standby/service/standby.service';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTER_LINKS } from '../../consts/app-keys.const';
import packageJson from '../../../../../package.json';
import { useDarkSurface } from './dark-surface';
import subdivsLogo from '../../../../assets/images/squarebit-subdivs/subdivs-logo.svg';
import subdivsLogoDark from '../../../../assets/images/squarebit-subdivs/subdivs-logo-dark.svg';
import { StandbyMark } from '../icons/standby-mark.component';
import { useRiserPage } from '../../../riser/feature/use-riser-page';
import { useHasFrameworkAccess } from '../../../sbframework/feature/use-sbframework-access';

// The panel can show more than one action now, so the chevron is its own thing
// rather than inline SVG repeated per button.
const ActionArrow = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M3 6h6M6.5 3.5 9 6l-2.5 2.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ProductsDropdownProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

type Product = {
  key: string;
  name: string;
  version?: string;
  description: string;
  tags: string[];
  valueProp: string;
  to: string;
  // Where the panel's Documentation action goes. Products send people to the
  // docs rather than the marketing page: anyone who opened this menu and
  // hovered a product has already seen the pitch. Omit it and no Documentation
  // link is shown at all — there is no fallback, because a link labelled
  // "Documentation" that goes somewhere else is worse than no link.
  docsTo?: string;
  // An optional second action for products that have somewhere else worth
  // going. Only TouchPose does today.
  secondary?: { labelKey: string; to: string };
  icon?: React.ReactNode;
};

const ProductsDropdown: React.FC<ProductsDropdownProps> = ({
  isOpen,
  onOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // The artwork follows the surface it sits on, not the chosen theme: over
  // the landing reel the bar is dark even in light mode.
  const isDark = useDarkSurface();
  const [isHovered, setIsHovered] = useState(false);

  // Versions come from package.json — the same source of truth the product pages use.
  const subdivsVersion =
    (packageJson as { squarebitSubdivs?: { version?: string } }).squarebitSubdivs
      ?.version ?? t('management.product.unknownVersion');
  const eyeVersion =
    (packageJson as { squarebitEye?: { version?: string } }).squarebitEye?.version ??
    t('management.product.unknownVersion');
  // Standby ships from its own repo and its own release cycle, so its number
  // is not in this package.json like the others. Asked of the API instead,
  // which asks Standby and caches the answer. Long staleTime because a version
  // does not change while you are looking at a menu, and a failure resolves to
  // null, which renders exactly as the others do when their number is unknown.
  const { data: standbyVersion } = useQuery({
    queryKey: ['standby-version'],
    queryFn: () => standbyService.getVersion(),
    staleTime: 10 * 60 * 1000,
    retry: false
  });

  const touchposeVersion =
    (packageJson as { touchpose?: { version?: string } }).touchpose?.version ??
    t('management.product.unknownVersion');

  // Riser is unannounced. Its menu entry, down to the words in it, comes from
  // the same gated endpoint the product page does, so an account without the
  // grant gets no entry and there is nothing in this file for anyone else to
  // read either: the strings are not here, only the shape that holds them.
  const riser = useRiserPage();

  // The framework is invite-only, so the entry appears for granted accounts
  // only. Unlike Riser it is a long-standing product rather than an
  // unannounced one, so its copy lives in the i18n bundle like every other
  // product's: what the grant controls is who can download and run it, and
  // both of those are checked server-side.
  const { granted: hasFramework } = useHasFrameworkAccess();
  const frameworkVersion =
    (packageJson as { sbframework?: { version?: string } }).sbframework?.version ??
    t('management.product.unknownVersion');

  // To add a product, add an entry here — the rail, panel and keyboard nav all
  // derive from this list. Services are no longer in here: they have their own
  // top-level nav item, which is a shorter path to a page people arrive
  // looking for rather than browse into.
  const PRODUCTS: Product[] = [
    {
      key: 'squarebit-subdivs',
      name: 'Subdivs',
      version: `v${subdivsVersion}`,
      description: t('products.squarebitSubdivsDescription'),
      tags: [
        'Unreal Engine',
        t('products.tagRealtime'),
        t('products.tagSubdivision')
      ],
      valueProp: t('products.squarebitSubdivsValueProp'),
      to: ROUTER_LINKS.SQUAREBIT_SUBDIVS,
      docsTo: ROUTER_LINKS.SQUAREBIT_SUBDIVS_DOCS,
      // Theme-aware badge: dark-badge variant on light bg, light-badge on dark bg.
      // The art has ~10% transparent padding and only a slight corner radius, so at
      // this size it reads square — scale the badge to fill the box and clip it with a
      // rounded container so the rounding is visible (dropdown-only; hero art unchanged).
      icon: (
        <span className="block w-full h-full overflow-hidden rounded-[5px]">
          <img
            src={isDark ? subdivsLogoDark : subdivsLogo}
            alt=""
            className="w-full h-full scale-125"
          />
        </span>
      )
    },
    {
      key: 'squarebit-eye',
      name: 'Eye',
      version: `v${eyeVersion}`,
      description: t('products.squarebitEyeDescription'),
      tags: ['Maya', 'Unreal Engine', 'Blender', t('products.tagCrossDcc')],
      valueProp: t('products.squarebitEyeValueProp'),
      to: ROUTER_LINKS.SQUAREBIT_EYE,
      docsTo: ROUTER_LINKS.SQUAREBIT_EYE_DOCS,
      // Grayscale so the emoji reads as a monochrome mark, not a colored icon.
      icon: <span className="text-[15px] leading-none grayscale">👁️</span>
    },
    {
      key: 'touchpose',
      name: 'TouchPose',
      version: `v${touchposeVersion}`,
      description: t('products.touchposeDescription'),
      tags: ['Maya', t('products.tagRigging'), t('products.tagAnimation')],
      valueProp: t('products.touchposeValueProp'),
      to: ROUTER_LINKS.TOUCHPOSE,
      docsTo: ROUTER_LINKS.TOUCHPOSE_DOCS,
      // The rig library is TouchPose's, and this is the only product with one.
      secondary: {
        labelKey: 'products.exploreLibrary',
        to: ROUTER_LINKS.MODELS
      },
      // Theme-aware: the light icon's black glyph vanishes on the dark dropdown, so
      // swap to the dark variant (glyph recolored to #DADAE0) in dark mode.
      icon: (
        <img
          src={`${process.env.PUBLIC_URL || ''}/${
            isDark ? 'touchpose_icon_dark.svg' : 'touchpose_icon.svg'
          }`}
          alt=""
          className="w-full h-full object-contain"
        />
      )
    },
    {
      key: 'standby',
      name: 'Standby',
      version: standbyVersion ? `v${standbyVersion}` : undefined,
      description: t('products.standbyDescription'),
      // Not 'SaaS'. The other two tags say where it runs and how it behaves;
      // SaaS describes the billing model, which is our concern rather than the
      // reader's, and is self-evident for a product you open in a browser. In a
      // menu whose other three entries are DCC plugins, 'no install' is the
      // thing about Standby that is actually worth a pill.
      tags: [
        t('products.tagWeb'),
        t('products.tagRealtime'),
        t('products.tagNoInstall')
      ],
      valueProp: t('products.standbyValueProp'),
      to: ROUTER_LINKS.STANDBY,
      // No docsTo: Standby has no docs site yet, so the panel shows no
      // Documentation link rather than one that leads back to this page.
      icon: <StandbyMark size={18} />
    }
  ];

  if (hasFramework) {
    PRODUCTS.push({
      key: 'sbframework',
      name: 'Framework',
      version: `v${frameworkVersion}`,
      description: t('products.sbframeworkDescription'),
      tags: ['Maya', t('products.tagRigging'), t('products.tagPipeline')],
      valueProp: t('products.sbframeworkValueProp'),
      to: ROUTER_LINKS.SBFRAMEWORK,
      docsTo: ROUTER_LINKS.SBFRAMEWORK_DOCS,
      icon: <span className="text-[15px] leading-none grayscale">⚙️</span>
    });
  }

  if (riser.page) {
    PRODUCTS.push({
      key: 'riser',
      name: riser.page.nav.name,
      version: riser.page.version ? `v${riser.page.version}` : undefined,
      description: riser.page.nav.description,
      tags: riser.page.nav.tags,
      valueProp: riser.page.nav.valueProp,
      to: ROUTER_LINKS.RISER,
      // No docsTo: Riser's documentation is inside the app, under Help, and a
      // link labelled Documentation that lands back on this page is worse
      // than no link. Grayscale, so the emoji reads as a mark rather than an
      // ornament, matching the Eye entry.
      icon: <span className="text-[15px] leading-none grayscale">📍</span>
    });
  }

  // The product whose page we're currently on — EXACT match only. Docs,
  // changelog, EULA and other subpages do not count as "on that product page".
  const currentProductKey =
    PRODUCTS.find((p) => location.pathname === p.to)?.key ?? null;

  const [activeKey, setActiveKey] = useState<string | null>(currentProductKey);
  // The detail panel always previews something; the row highlight does not.
  const active = PRODUCTS.find((p) => p.key === activeKey) ?? PRODUCTS[0];
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // On open, highlight only the product page we're on — no highlight elsewhere.
  useEffect(() => {
    if (isOpen) setActiveKey(currentProductKey);
  }, [isOpen, currentProductKey]);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  const onRowKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next =
        e.key === 'ArrowDown'
          ? Math.min(index + 1, PRODUCTS.length - 1)
          : Math.max(index - 1, 0);
      setActiveKey(PRODUCTS[next].key);
      rowRefs.current[next]?.focus();
    } else if (e.key === 'Escape') {
      onClose();
    }
    // Enter / Space activate the focused button via its native onClick.
  };

  const renderRow = (p: Product) => {
    const index = PRODUCTS.findIndex((x) => x.key === p.key);
    const isActive = p.key === activeKey;
    return (
      <button
        key={p.key}
        ref={(el) => {
          rowRefs.current[index] = el;
        }}
        type="button"
        onClick={() => go(p.to)}
        onMouseEnter={() => setActiveKey(p.key)}
        onFocus={() => setActiveKey(p.key)}
        onKeyDown={(e) => onRowKeyDown(e, index)}
        aria-current={p.key === currentProductKey ? 'page' : undefined}
        className={`w-full flex items-center gap-[10px] h-[36px] px-[10px] rounded-[8px] cursor-pointer text-left transition-colors duration-100 ${isActive ? 'bg-surface-hover' : 'bg-transparent'}`}
      >
        {/* Icon column (fixed width so names align even for icon-less rows).
            The colour is set here rather than inherited: Standby's mark draws
            its neutral dots in currentColor, and without a stated colour it
            picked up whatever the row happened to carry — which was not the
            content colour, so the mark did not follow the theme. */}
        <span
          aria-hidden="true"
          className="shrink-0 w-[20px] h-[20px] flex items-center justify-center text-content-primary"
        >
          {p.icon}
        </span>
        <span className="flex-1 font-body text-[14px] font-semibold truncate text-content-primary">
          {p.name}
        </span>
        {p.version && (
          <span className="shrink-0 font-mono text-[11px] text-content-muted-alt tabular-nums">
            {p.version}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="relative">
      {/* Trigger button */}
      <button
        onClick={() => (isOpen ? onClose() : onOpen())}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        className={`font-body text-medium rounded-full px-[16px] py-[7px] border-none cursor-pointer flex items-center gap-[4px] transition-all duration-150 ${isOpen || isHovered ? 'text-blue bg-surface-hover' : 'text-base-black bg-transparent'}`}
      >
        {t('header.products')}
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          className={`transition-transform duration-150 shrink-0 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Two-pane dropdown */}
      {isOpen && (
        <div className="absolute top-[calc(100%+10px)] left-0 z-[200]">
          <div className="animate-dropdown flex bg-surface-base border-[1.5px] border-surface-hover rounded-[16px] p-[8px] shadow-[0_12px_28px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)] font-body">
            {/* Left rail — same white bg as the panel, separated by a hairline */}
            <div className="w-[240px] shrink-0 flex flex-col gap-[2px] pr-[10px] border-r border-border-dark/[0.08]">
              <div
                className="px-[10px] pt-[2px] pb-[6px] font-body text-[10px] font-bold tracking-[0.08em] uppercase"
                style={{ color: 'var(--color-brand-link)' }}
              >
                {t('products.groupProducts')}
              </div>
              {PRODUCTS.map(renderRow)}
            </div>

            {/* Right detail panel */}
            <div
              aria-live="polite"
              className="w-[326px] shrink-0 min-h-[236px] pl-[18px] pr-[8px] py-[8px]"
            >
              <div className="font-body text-[17px] font-semibold text-content-primary tracking-[-0.2px] mb-[7px]">
                {active.name}
              </div>
              <p className="font-body text-[14px] text-content-secondary leading-[1.6] mb-[13px]">
                {active.description}
              </p>
              <div className="flex flex-wrap gap-[6px] mb-[14px]">
                {active.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-surface-section rounded-[5px] px-[8px] py-[3px] font-body text-[12px] text-content-muted-alt"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              {/* Actions. The product name in the rail is already the link to
                  the product page, so these go somewhere else useful — and a
                  product with nowhere else to go shows none.

                  Documentation used to render for everything, falling back to
                  the product page when a product had no docs. That made it a
                  link labelled "Documentation" that went somewhere that is not
                  documentation, and the row it sat in was the page you were
                  already being offered. Standby is the only product in that
                  position today; keying off docsTo rather than the product's
                  name means the link returns by itself if it ever gets docs. */}
              <div className="flex flex-wrap items-center gap-x-[18px] gap-y-[6px] empty:hidden">
                {active.docsTo && (
                  <button
                    type="button"
                    onClick={() => go(active.docsTo!)}
                    className="inline-flex items-center gap-[5px] font-body text-[13px] font-medium text-blue cursor-pointer hover:gap-[8px] transition-all duration-150"
                  >
                    {t('products.documentation')}
                    <ActionArrow />
                  </button>
                )}
                {active.secondary && (
                  <button
                    type="button"
                    onClick={() => go(active.secondary!.to)}
                    className="inline-flex items-center gap-[5px] font-body text-[13px] font-medium text-blue cursor-pointer hover:gap-[8px] transition-all duration-150"
                  >
                    {t(active.secondary.labelKey)}
                    <ActionArrow />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsDropdown;
